import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { DeviceClasses } from '@racemap/utilities/consts/events';
import { uuid } from '@racemap/utilities/functions/utils';
import type { KeyData, RacemapEvent } from '@racemap/utilities/types/events';
import type { Point } from '@racemap/utilities/types/types';
import _ from 'lodash';
import moment from 'moment';
import { type ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Accordion, Alert, Button, Card, Col, Container, FormLabel, Row } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import ButtonWithSpinner, { Tristate, tristateTask } from '../../components/ButtonWithSpinner';
import CheckKeyInput from '../../components/CheckKeyInput';
import FieldGroup from '../../components/FieldGroup';
import {
  IconArrowDown,
  IconArrowRight,
  IconExternal,
  IconGear,
  IconSetting,
} from '../../components/Icon';
import { HelperModal } from '../../components/Modal';
import TrackDropView from '../../components/TrackDropView';
import YoutubeIframe from '../../components/YoutubeIframe';
import { toastError, toastSuccess } from '../../components/utils/Utils';

const apiClient = RacemapAPIClient.fromWindowLocation();

export function UploadLandingView() {
  const [savingState, setSavingState] = useState(Tristate.NORMAL);
  const [shouldOpenPlayer, setShouldOpenPlayer] = useState(false);
  const [enteredKey, setEnteredKey] = useState('');
  const [enteredStartNumber, setEnteredStartNumber] = useState('');
  const [enteredName, setEnteredName] = useState('');
  const [keyData, setKeyData] = useState<KeyData>({
    name: '',
    startNumber: '',
    eventId: '',
    eventName: '',
    keyUsed: false,
  });
  const [eventData, setEventData] = useState<RacemapEvent | null>(null);
  const [track, setTrack] = useState<{ points: Array<Point>; name: string } | null>(null);
  const dateCheck =
    new Date() < moment(eventData?.endTime).toDate() ||
    eventData?.modules?.timing.visibleElements.participateAfterEvent ||
    eventData == null;
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  useEffect(() => {
    if (location.search !== '') {
      const matchEnteredKey = urlParams.get('key')?.match(/^([a-zA-Z0-9]{6})$/);
      if (matchEnteredKey != null) {
        setEnteredKey(matchEnteredKey[1].toUpperCase());
      }
      const matchEnteredStartNumber = urlParams.get('startNumber');
      if (matchEnteredStartNumber != null) {
        setEnteredStartNumber(matchEnteredStartNumber);
      }
      const matchEnteredName = urlParams.get('name');
      if (matchEnteredName != null) {
        setEnteredName(matchEnteredName);
      }
    }
  }, [location.search]);

  const metaDataTrack: { pointsOutsideEvent: Array<Point>; firstTime: Date } | null =
    useMemo(() => {
      if (track == null) return null;
      if (eventData == null)
        return {
          pointsOutsideEvent: track.points,
          firstTime: track.points[0].time,
        };

      const startTime = Date.parse(eventData.startTime);
      const endTime = Date.parse(eventData.endTime);
      return {
        pointsOutsideEvent: track.points.filter((p) => {
          return p.time.getTime() < startTime || p.time.getTime() > endTime;
        }),
        firstTime: track.points[0].time,
      };
    }, [track, eventData]);

  async function handleReceivedKey(info: KeyData) {
    if (info.eventId !== '') {
      setEventData(await apiClient.getEvent(info.eventId));
    }
    setKeyData(info);
  }

  async function handleUploadRequest() {
    if (!readyToUpload()) return;
    tristateTask(
      async () => {
        let appId = null;
        let starter = null;

        if (keyData.keyUsed && keyData.starterId != null) {
          starter = await apiClient.getStarter(keyData.starterId, { key: enteredKey });
          if (starter.appId == null || starter.appId === '') {
            appId = uuid();
            await apiClient.linkEventStarterKey(enteredKey.toUpperCase(), appId, {
              id: starter.id,
              appId,
            });
          } else {
            appId = starter.appId;
          }
        } else {
          appId = uuid();

          // Create starter and attach to event through key
          await apiClient.linkEventStarterKey(enteredKey.toUpperCase(), appId, {
            deviceClass: DeviceClasses.Upload,
            deviceType: '',
            name: keyData.name == null && enteredName != null ? enteredName : keyData.name,
            startNumber:
              keyData.startNumber == null && enteredStartNumber != null
                ? enteredStartNumber
                : keyData.startNumber,
            appId,
          });
        }

        if (starter == null) {
          const newKeyData = await apiClient.checkEventStarterKey(enteredKey.toUpperCase(), {
            usedOk: true,
          });
          if (newKeyData.starterId != null) {
            starter = await apiClient.getStarter(newKeyData.starterId);
          }
        }

        // update startTime of the participant
        if (
          starter != null &&
          metaDataTrack?.firstTime != null &&
          (starter.startTime == null ||
            metaDataTrack.firstTime.getTime() < Date.parse(starter.startTime))
        ) {
          await apiClient.updateStarter(starter.id, {
            startTime: metaDataTrack?.firstTime.toISOString(),
          });
        }

        // Upload points
        if (track?.points != null) {
          await apiClient.addPoints({ appId, points: track.points });
        }

        toastSuccess('Track successfully uploaded. Check out your track in the Tracking Map.');
        setShouldOpenPlayer(true);
      },
      (state: Tristate) => setSavingState(state),
    ).then(null, () => {
      toastError("Couldn't upload track");
    });
  }

  function readyToUpload() {
    return (
      enteredKey != null &&
      track != null &&
      track.points.length > 0 &&
      keyData.eventId != null &&
      (!_.isEmpty(keyData.startNumber) || !_.isEmpty(enteredStartNumber)) &&
      (!_.isEmpty(keyData.name) || !_.isEmpty(enteredName)) &&
      dateCheck
    );
  }

  function UploadHelperModal() {
    return (
      <HelperModal title="How to get your Activity" size="lg">
        <Accordion defaultActiveKey="uploadActivity">
          <Card>
            <Card.Header>
              <Accordion.Toggle as={Button} variant="link" eventKey="0">
                <h6 className="mb-0">Garmin</h6>
              </Accordion.Toggle>
            </Card.Header>
            <Accordion.Collapse eventKey="0">
              <Card.Body>
                <ol>
                  <div className="my-3">
                    <li>
                      Log in to Garmin connect on your computer.{' '}
                      <a
                        href="https://connect.garmin.com"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        (https://connect.garmin.com)
                      </a>
                    </li>
                    <li>
                      Click on the link &quot;Activities&quot; <IconArrowRight /> &quot;All
                      Activities&quot; to get a list of all your activities
                    </li>
                    <li>Click on the desired activities</li>
                    <li>
                      Click on the tool icon (<IconGear />) on the top right
                    </li>
                    <li>Select &quot;Export as GPX file&quot;</li>
                  </div>
                  The activity is saved as a file on your pc.
                </ol>
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card>
            <Card.Header>
              <Accordion.Toggle as={Button} variant="link" eventKey="1">
                <h6 className="mb-0">Polar</h6>
              </Accordion.Toggle>
            </Card.Header>
            <Accordion.Collapse eventKey="1">
              <Card.Body>
                <ol>
                  <div className="my-3">
                    <li>
                      Log in to Polar Flow on your computer.{' '}
                      <a href="https://flow.polar.com" target="_blank" rel="noopener noreferrer">
                        (https://flow.polar.com)
                      </a>
                    </li>
                    <li>
                      Click on the link &quot;Training History&quot; to get a list of all your
                      activities
                    </li>
                    <li>Click on the desired activities</li>
                    <li>Click on &quot;Export Session&quot;</li>
                    <li>Select &quot;Session (TCX)&quot;</li>
                  </div>
                  The activity is saved as a file on your pc.
                </ol>
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card>
            <Card.Header>
              <Accordion.Toggle as={Button} variant="link" eventKey="2">
                <h6 className="mb-0">Endomondo</h6>
              </Accordion.Toggle>
            </Card.Header>
            <Accordion.Collapse eventKey="2">
              <Card.Body>
                <ol>
                  <div className="my-3">
                    <li>
                      Log in to Endomodo on your computer.{' '}
                      <a href="https://endomondo.com" target="_blank" rel="noopener noreferrer">
                        (https://endomondo.com)
                      </a>
                    </li>
                    <li>Go to Training History to see your activities</li>
                    <li>Click on the desired activities</li>
                    <li>
                      Click on the arrow down (<IconArrowDown />) next to the title of your activity
                    </li>
                    <li>Select &quot;Export&quot;.</li>
                    <li>Select &quot;GPX format&quot;.</li>
                    <li>Click on the &quot;Export&quot; button</li>
                  </div>
                  The activity is saved as a file on your pc.
                </ol>
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card>
            <Card.Header>
              <Accordion.Toggle as={Button} variant="link" eventKey="3">
                <h6 className="mb-0">adidas Running by Runtastic</h6>
              </Accordion.Toggle>
            </Card.Header>
            <Accordion.Collapse eventKey="3">
              <Card.Body>
                <ol>
                  <div className="my-3">
                    <li>
                      Log in to adidas Running by Runtastic on your computer.{' '}
                      <a
                        href="https://runtastic.com/login"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        (https://www.runtastic.com/login)
                      </a>
                    </li>
                    <li>Click on the desired activities</li>
                    <li>Click on the Settings icon next to the Edit button</li>
                    <li>Select &quot;Export as .gpx file&quot;</li>
                  </div>
                  The activity is saved as a file on your pc.
                </ol>
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card>
            <Card.Header>
              <Accordion.Toggle as={Button} variant="link" eventKey="4">
                <h6 className="mb-0">Strava</h6>
              </Accordion.Toggle>
            </Card.Header>
            <Accordion.Collapse eventKey="4">
              <Card.Body>
                <ol>
                  <div className="my-3">
                    <li>
                      Log in to Strava on your computer.{' '}
                      <a href="https://strava.com" target="_blank" rel="noopener noreferrer">
                        (https://www.strava.com)
                      </a>
                    </li>
                    <li>Go to your profile to see your activities</li>
                    <li>Click on the desired activities</li>
                    <li>
                      Click on the Settings icon (<IconSetting />) on the left
                    </li>
                    <li>Select &quot;Export GPX&quot;.</li>
                  </div>
                  The activity is saved as a file on your pc.
                </ol>
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card>
            <Card.Header>
              <Accordion.Toggle as={Button} variant="link" eventKey="5">
                <h6 className="mb-0">Under Amour</h6>
              </Accordion.Toggle>
            </Card.Header>
            <Accordion.Collapse eventKey="5">
              <Card.Body>
                <ol>
                  <div className="my-3">
                    <li>
                      Log in to Under Armour on your computer. (
                      <a href="https://mapmyrun.com" target="_blank" rel="noopener noreferrer">
                        https://www.mapmyrun.com
                      </a>{' '}
                      /{' '}
                      <a href="https://mapmwalk.com" target="_blank" rel="noopener noreferrer">
                        https://www.mapmwalk.com
                      </a>{' '}
                      /{' '}
                      <a href="https://mapmyride.com" target="_blank" rel="noopener noreferrer">
                        https://www.mapmyride.com
                      </a>{' '}
                      /{' '}
                      <a href="https://mapmyhike.com" target="_blank" rel="noopener noreferrer">
                        https://www.mapmyhike.com
                      </a>
                      )
                    </li>
                    <li>Go to your profile to see your activities</li>
                    <li>Click on the desired activities</li>
                    <li>Click on the TCX button</li>
                  </div>
                  The activity is saved as a file on your pc.
                </ol>
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        </Accordion>
        <p>
          <YoutubeIframe videoId="JloKXFsO5xM" />
        </p>
      </HelperModal>
    );
  }

  const keyIsEmpty = ['/upload', '/upload/'].indexOf(location.pathname) !== -1;

  return (
    <Container>
      <Row>
        <Col xs={12} lg={{ span: 4, offset: 4 }}>
          <h2>Upload Track</h2>
          <p>
            Use your sports tracker such as Garmin, Polar or Suunto and upload recorded activities
            into a specific Tracking Map. Your uploaded geolocations data will be used for the
            activity comparison of many participants. For that reason, the geolocations are analyzed
            in a conservative manner. That means your geolocations might result in a bit less
            distance respectively a bit longer time in comparison to 3rd party applications.
            <HelperModal title={'Plausibility Check'}>
              <ul>
                <div className="my-3">
                  <li>
                    Unrealistic speed like 60 km/h for an ultra trail. Racemap ignores these
                    geolocations. Practical relevance: if a participant forgets to stop tracking the
                    activity while going by car, the car ride will be ignored automatically.
                  </li>
                  <li>
                    Wrong timestamps: If uploaded geolocations are not within the start and end time
                    of the event, the segment will be ignored.
                  </li>
                  <li>
                    GPS sensitivity: Garmin, Strava, etc. provide their geolocation data with an
                    accuracy of about 10%.
                  </li>
                </div>
              </ul>
            </HelperModal>
          </p>
          <p>
            Supported formats: GPX, KML and TCX.
            <UploadHelperModal />
          </p>
          <p>
            Enter the Passcode or a Tracking Key to select the correct Tracking map. Using the same
            Tracking Key you can upload multiple activities or segments into one Tracking Map.
          </p>
        </Col>
      </Row>
      <Row>
        <Col xs={12} lg={{ span: 4, offset: 4 }}>
          <CheckKeyInput
            starterKey={enteredKey}
            usedOk
            onReceivedKey={handleReceivedKey}
            disabled={!keyIsEmpty || shouldOpenPlayer}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setEnteredKey(event.target.value.toUpperCase());
            }}
            id="racemap_keylanding_key_input"
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12} lg={{ span: 4, offset: 4 }}>
          <FieldGroup
            id="event_name_input"
            label="Tracking Map:"
            value={keyData.eventName}
            disabled
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12} lg={{ span: 4, offset: 4 }}>
          <FieldGroup
            id="start_number_input"
            label="Start Number:"
            value={
              keyData.startNumber == null && enteredStartNumber != null
                ? enteredStartNumber
                : keyData.startNumber
            }
            disabled={keyData.starterId != null}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setKeyData({ ...keyData, startNumber: event.target.value });
            }}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12} lg={{ span: 4, offset: 4 }}>
          <FieldGroup
            id="starter_name_input"
            label="Name:"
            value={keyData.name == null && enteredName != null ? enteredName : keyData.name}
            disabled={keyData.starterId != null}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setKeyData({ ...keyData, name: event.target.value });
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} lg={{ span: 4, offset: 4 }}>
          <FormLabel>Track:</FormLabel>
          {track != null &&
            metaDataTrack != null &&
            track.points.length > 0 &&
            metaDataTrack.pointsOutsideEvent.length > 0 &&
            (track.points.length === metaDataTrack.pointsOutsideEvent.length ? (
              <Alert variant="danger">
                All {<b>{metaDataTrack.pointsOutsideEvent.length}</b>} points of your uploaded track
                are outside the start and end time of the Tracking Map.
              </Alert>
            ) : (
              <Alert variant="warning">
                The uploaded track contains {<b>{metaDataTrack.pointsOutsideEvent.length}</b>}{' '}
                points outside the start and end time of the Tracking Map.
              </Alert>
            ))}
          <TrackDropView
            id="racemap_track_upload"
            value={track}
            onChange={(newTrack) => {
              setTrack(newTrack);
            }}
          />
        </Col>
      </Row>
      {!dateCheck && (
        <Row>
          <Col xs={12} lg={{ span: 4, offset: 4 }}>
            <Alert variant="danger">
              Your event is over. You can&apos;t contribute your activity anymore.
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col xs={12} lg={{ span: 4, offset: 4 }} className="text-center">
          {shouldOpenPlayer ? (
            <Button variant="primary" href={`/timing/${keyData.eventId}`}>
              <IconExternal /> Open Leaderboard
            </Button>
          ) : (
            <ButtonWithSpinner
              id="racemap_keylanding_button"
              label="Upload"
              buttonState={savingState}
              disabled={!readyToUpload()}
              onClick={handleUploadRequest}
            />
          )}
        </Col>
      </Row>
    </Container>
  );
}
